/* eslint-disable no-invalid-this */
import Backbone from "backbone";
import gql from "graphql-tag";

import {gqlOp} from "@khan/data-gql-core";
import Log, {Errors, KAError} from "@khan/logging";

// eslint-disable-next-line no-restricted-imports
import {getUnsafeApolloGqlClient} from "#data-access/gql/get-unsafe-apollo-gql-client.ts";
import Profile from "#profile-nav-package/profile.tsx";

const SET_PROFILE_BADGES = gqlOp(gql`
    mutation SetProfileBadges($kaid: String!, $badgeNames: [String!]!) {
        setProfileBadges(kaid: $kaid, badgeNames: $badgeNames) {
            error {
                code
            }
        }
    }
`);

/**
 * @enum {number}
 */
const Badges: any = {
    ContextType: {
        NONE: 0,
        EXERCISE: 1,
        PLAYLIST: 2,
    },
};

/**
 * @enum {number}
 */
Badges.Category = {
    BRONZE: 0, // Meteorite, "Common"
    SILVER: 1, // Moon, "Uncommon"
    GOLD: 2, // Earth, "Rare"
    PLATINUM: 3, // Sun, "Epic"
    DIAMOND: 4, // Black Hole, "Legendary"
    MASTER: 5, // Topic/Academic Achievement
};

/**
 * A single badge that a user can earn.
 * Parallel to the JSON serialized formats of badges.Badge
 */
Badges.Badge = Backbone.Model.extend({
    defaults: {
        badgeCategory: Badges.Category.BRONZE,
        name: "__empty__",
        description: "",
        icons: {},
        isOwned: false,
        points: 0,
        translatedExtendedDescription: "",
    },

    isEmpty: function (this: any) {
        // Specially reserved name for empty badge slots.
        // Used in display case - must be synced with what the server
        // understands in util_badges.py
        return this.get("name") === "__empty__";
    },

    toJSON: function (this: any) {
        const json = Badges.Badge.__super__.toJSON.call(this);
        json["isEmpty"] = this.isEmpty();
        return json;
    },
});

/**
 * A re-usable instance of an empty badge.
 */
Badges.Badge.EMPTY_BADGE = new Badges.Badge({});

/**
 * Badge information about a badge, or a set of badges that a user has earned
 * grouped by their badge type.
 * Parallel to the JSON serialized formats of badges.GroupedUserBadge
 */
Badges.UserBadge = Backbone.Model.extend({
    defaults: {
        badge: null,
        isOwned: true,
        count: 1,
        lastEarnedDate: "2011-11-22T00:00:00Z",
    },

    initialize: function (this: any, attributes: any, options: any) {
        if (!this.get("badge")) {
            throw new KAError(
                "A UserBadge object needs a reference badge object",
                Errors.Internal,
            );
        }

        // Wrap the underlying badge info in a Model object and forward
        // change events.
        const badgeModel = new Badges.Badge(this.get("badge"));
        this.set({badge: badgeModel}, {silent: true});
        badgeModel.bind(
            "change",
            function (this: any, ev) {
                this.trigger("change:badge");
            },
            this,
        );
    },
});

/**
 * A list of badges that can be listened to.
 * This list can be edited by adding or removing from the collection,
 * and saved up to a server.
 */
Badges.BadgeList = Backbone.Collection.extend({
    model: Badges.Badge,

    saveUrl: null,

    /**
     * Whether or not this badge list has been modified since the last
     * save to the server.
     */
    dirty_: false,

    toJSON: function (this: any) {
        return this.map(function (badge) {
            return badge.get("name");
        });
    },

    add: function (this: any, models, options) {
        // eslint-disable-next-line prefer-rest-params
        Badges.BadgeList.__super__.add.apply(this, arguments);
        this.dirty_ = true;
    },

    remove: function (this: any, models, options) {
        // eslint-disable-next-line prefer-rest-params
        Badges.BadgeList.__super__.remove.apply(this, arguments);
        this.dirty_ = true;
    },

    /**
     * Saves the collection to the server.
     * This does *not* save any individual edits to Badges within this list;
     * it simply posts the information about what belongs in the set.
     */
    save: function (this: any) {
        if (!this.dirty_) {
            return;
        }

        // TODO(somewhatabstract, JIRA-XXX1): Improve our redux patterns so that we
        // can pass a valid ApolloGqlClient from the react components.
        // eslint-disable-next-line no-restricted-syntax
        const gqlClient = getUnsafeApolloGqlClient();

        const update = {
            kaid: Profile.getBaseRequestParams().kaid,
            badgeNames: this.map(function (badge) {
                return badge.get("name");
            }),
        } as const;

        gqlClient
            // @ts-expect-error - TS2345 - Argument of type 'GqlOperation<unknown, Empty>' is not assignable to parameter of type 'GqlOperation<Record<any, any>, Empty>'.
            .mutate(SET_PROFILE_BADGES, {
                variables: update,
            })
            .then((resp) => {
                if (resp?.data?.setProfileBadges?.error?.code) {
                    Log.error(
                        "Failed to save profile widget badges",
                        Errors.Internal,
                        // @ts-expect-error - TS2559 - Type 'ApolloResult<Record<any, any>>' has no properties in common with type 'LogErrorOptions'.
                        resp,
                    );
                }
            })
            .catch((e) => {
                Log.error(
                    "Failed to save profile widget badges",
                    Errors.Internal,
                    {cause: e},
                );
            });

        this.dirty_ = false;
    },

    // TODO: figure out how to do this in a more systematic way!
    // Override base Backbone.parse since badge modifications can result in
    // api_action_results to be sent back.
    parse: function (this: any, resp, xhr) {
        if ("apiActionResults" in resp && "payload" in resp) {
            resp = resp["payload"];
        }
        Backbone.Model.prototype.parse.call(this, resp, xhr);
    },
});

/**
 * A list of user badges that can be listened to.
 */
Badges.UserBadgeList = Backbone.Collection.extend({
    model: Badges.UserBadge,
});

export default Badges;
